import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, FormContainer, Modal, Text, Title } from '@wyz/ui';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import {
  CriteriaEditableBadge,
  CriteriaValueInput,
} from '~/connect/components';
import { createCriteriaFormSchema } from '~/connect/schemas';
import { ICreateCriteriaForm } from '~/connect/types';

interface CreateCriteriaModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ICreateCriteriaForm) => void;
  isSubmitting?: boolean;
}

export const CreateCriteriaModal: React.FC<CreateCriteriaModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const { t } = useI18n();

  const methods = useForm<ICreateCriteriaForm>({
    resolver: zodResolver(createCriteriaFormSchema),
    defaultValues: {
      name: '',
      values: [],
      visible: false,
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: 'values',
  });

  const handleSubmit = (data: ICreateCriteriaForm) => {
    onSubmit(data);
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal.Container open={open} onClose={handleClose} size='lg'>
      <Modal.Header variant='light'>
        <Title heading='page-title' weight='bold'>
          <Trans i18nKey='app.criteria.create.modal.title' />
        </Title>
      </Modal.Header>
      <FormContainer methods={methods} submitHandler={handleSubmit}>
        <Modal.Body>
          <div className='d-flex flex-column gap-4'>
            <div className='d-flex flex-column gap-3'>
              <Text heading='normal' weight='semi-bold'>
                <Trans i18nKey='app.criteria.create.modal.criteria_parameter' />
              </Text>
              <TextField
                name='name'
                label={t('app.criteria.create.modal.criteria_name')}
                placeholder={t(
                  'app.criteria.create.modal.criteria_name.placeholder',
                )}
                required
              />
            </div>

            <div className='d-flex flex-column gap-3'>
              <div className='d-flex flex-column gap-2'>
                <Text color='secondary' heading='sm'>
                  <Trans i18nKey='app.criteria.create.modal.values' />
                </Text>
                <Flex wrap='wrap' className='gap-2 align-items-center'>
                  {fields.map((field, index) => (
                    <CriteriaEditableBadge.Root
                      key={field.id}
                      value={field.label}
                      onSubmit={(newValue) =>
                        update(index, { label: newValue })
                      }
                    >
                      <CriteriaEditableBadge.Display
                        value={field.label}
                        reserved={false}
                        isDefault={false}
                        onDelete={() => remove(index)}
                      />
                      <CriteriaEditableBadge.Input
                        name={`values.${index}.label`}
                      />
                    </CriteriaEditableBadge.Root>
                  ))}
                  <CriteriaValueInput.Root
                    onSubmit={(newValue) => {
                      append({ label: newValue });
                    }}
                  >
                    <CriteriaValueInput.Trigger id={`add-new-value`} />
                    <CriteriaValueInput.Input name='newValue' />
                  </CriteriaValueInput.Root>
                </Flex>
                {methods.formState.errors.values && (
                  <Text color='danger' heading='xs'>
                    <Trans i18nKey='app.criteria.create.modal.values.required' />
                  </Text>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer variant='light'>
          <Flex className='w-100 gap-3'>
            <Button
              id='cancel-create-criteria'
              type='button'
              variant='secondary'
              className='flex-grow-1'
              onClick={handleClose}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id='submit-create-criteria'
              type='submit'
              variant='primary'
              className='flex-grow-1'
              disabled={isSubmitting}
            >
              <Trans i18nKey='app.common.buttons.validate' />
            </Button>
          </Flex>
        </Modal.Footer>
      </FormContainer>
    </Modal.Container>
  );
};
